"use client";

import React, { useEffect, useRef, useState } from "react";

interface ScrollAnimationProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right";
  stagger?: boolean;
}

function getHiddenTransform(direction: ScrollAnimationProps["direction"]) {
  switch (direction) {
    case "down":
      return "translate3d(0, -30px, 0)";
    case "left":
      return "translate3d(30px, 0, 0)";
    case "right":
      return "translate3d(-30px, 0, 0)";
    case "up":
    default:
      return "translate3d(0, 30px, 0)";
  }
}

export function ScrollAnimation({
  children,
  className = "",
  delay = 0,
  direction = "up",
  stagger = false,
}: ScrollAnimationProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReduceMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);

    return () => {
      mediaQuery.removeEventListener("change", updatePreference);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      setIsVisible(true);
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          return;
        }

        setIsVisible(true);
        observer.disconnect();
      },
      {
        rootMargin: "0px 0px -50px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [reduceMotion]);

  const baseStyle: React.CSSProperties | undefined = reduceMotion
    ? undefined
    : {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translate3d(0, 0, 0)" : getHiddenTransform(direction),
        filter: isVisible ? "blur(0px)" : "blur(4px)",
        transitionProperty: "opacity, transform, filter",
        transitionDuration: "600ms",
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
        transitionDelay: `${delay}s`,
        willChange: isVisible ? "auto" : "opacity, transform, filter",
      };

  if (!stagger) {
    return (
      <div ref={ref} className={className} style={baseStyle}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, index) => (
        <div
          style={
            reduceMotion
              ? undefined
              : {
                  ...baseStyle,
                  transitionDelay: `${delay + index * 0.1}s`,
                }
          }
        >
          {child}
        </div>
      ))}
    </div>
  );
}
